/**
 * Non–windscreen claim subtype cards (extensible list).
 * @module claims/otherClaimSubtypeOptions
 */

/** One selectable “type of claim” after the user chooses any non–windscreen claim. */
export interface OtherClaimSubtypeOption {
  /** Stable id for URLs, storage, and analytics. */
  id: string
  headline: string
  caption: string
}

/** Legacy subtype id merged into the `single-vehicle` option; still accepted from drafts and URLs. */
export const LEGACY_OTHER_CLAIM_SUBTYPE_HIT_OBJECT_OR_ANIMAL_ID = 'hit-object-or-animal' as const

export const OTHER_CLAIM_SUBTYPE_OPTIONS: readonly OtherClaimSubtypeOption[] = [
  {
    id: 'multi-vehicle-collision',
    headline: 'Multi-vehicle collision',
    caption: 'Involving two or more moving vehicles',
  },
  {
    id: 'single-vehicle',
    headline: 'Single vehicle collision',
    caption: 'Only your vehicle was involved, including hitting a stationary object or an animal',
  },
  {
    id: 'weather',
    headline: 'Weather',
    caption: 'Storm, flood, hail or similar',
  },
  {
    id: 'theft',
    headline: 'Theft/Attempted theft',
    caption: 'Stolen vehicle or stolen property from the vehicle',
  },
  {
    id: 'fire',
    headline: 'Fire',
    caption: 'Fire damage to your vehicle',
  },
  {
    id: 'malicious-damage',
    headline: 'Malicious damage',
    caption: 'Deliberate damage to your vehicle by another person',
  },
  {
    id: 'other-loss',
    headline: 'Other',
    caption: 'Another type of loss not listed above',
  },
  {
    id: 'off-road',
    headline: 'Off-road recovery',
    caption: 'If your vehicle needs recovering or becomes unable to be driven while touring off road',
  },
] as const

export type OtherClaimSubtypeId = (typeof OTHER_CLAIM_SUBTYPE_OPTIONS)[number]['id']

const KNOWN_IDS = new Set<string>(OTHER_CLAIM_SUBTYPE_OPTIONS.map((o) => o.id))

/**
 * Parses {@link OTHER_CLAIM_SUBTYPE_QUERY_KEY} from claim URLs.
 * Maps {@link LEGACY_OTHER_CLAIM_SUBTYPE_HIT_OBJECT_OR_ANIMAL_ID} to `single-vehicle`.
 */
export function parseOtherClaimSubtypeQuery(raw: string | null): OtherClaimSubtypeId | null {
  if (raw === null) return null
  if (raw === LEGACY_OTHER_CLAIM_SUBTYPE_HIT_OBJECT_OR_ANIMAL_ID) return 'single-vehicle'
  if (KNOWN_IDS.has(raw)) return raw as OtherClaimSubtypeId
  return null
}

/** Short label for desktop progress rail summaries. */
export function formatOtherClaimSubtypeSummary(id: OtherClaimSubtypeId | null | undefined): string | undefined {
  if (!id) return undefined
  return OTHER_CLAIM_SUBTYPE_OPTIONS.find((o) => o.id === id)?.headline
}
